/**
 * Document Viewer Component
 * Displays detailed document information and metadata
 */
import type { Document, ChunkingStrategy } from '../../types';

interface DocumentViewerProps {
  document: Document | null;
  onClose: () => void;
}

export function DocumentViewer({ document, onClose }: DocumentViewerProps) {
  if (!document) return null;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getFormatName = (mimeType: string): string => {
    const formats: Record<string, string> = {
      'application/pdf': 'PDF',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'text/plain': 'Text',
      'text/markdown': 'Markdown',
      'text/html': 'HTML',
      'text/csv': 'CSV',
    };
    return formats[mimeType] || mimeType;
  };

  const getStrategyDescription = (strategy: ChunkingStrategy): string => {
    const descriptions: Record<ChunkingStrategy, string> = {
      FIXED_SIZE: 'Fixed character-length chunks with overlap',
      SEMANTIC: 'Sentence boundary detection using NLP',
      RECURSIVE: 'Hierarchical chunking (paragraphs → sentences)',
      SLIDING_WINDOW: 'Overlapping windows for context preservation',
      MARKDOWN_AWARE: 'Structure-preserving for markdown documents',
    };
    return descriptions[strategy] || strategy;
  };

  const getReadabilityColor = (level?: string): string => {
    if (!level) return 'text-gray-400';
    if (level.includes('Easy')) return 'text-green-400';
    if (level.includes('Standard')) return 'text-blue-400';
    if (level.includes('Difficult')) return 'text-orange-400';
    return 'text-gray-400';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-6 flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-semibold text-white mb-2 break-words">{document.name}</h2>
            <div className="flex items-center gap-3 text-sm text-gray-400">
              <span className="px-2 py-1 bg-gray-800 rounded">{getFormatName(document.mimeType)}</span>
              <span>{formatFileSize(document.size)}</span>
              <span>•</span>
              <span>{document.chunkCount} chunks</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Document Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoItem label="Document ID" value={document.id.slice(0, 8) + '...'} />
              <InfoItem label="Uploaded" value={formatDate(document.createdAt)} />
            </div>
          </div>

          {/* Metadata */}
          {document.metadata && (
            <>
              {/* Content Analysis */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Content Analysis</h3>
                <div className="grid grid-cols-2 gap-4">
                  <InfoItem label="Language" value={`${document.metadata.language.toUpperCase()}`} />
                  <InfoItem
                    label="Word Count"
                    value={document.metadata.wordCount.toLocaleString()}
                  />
                  <InfoItem
                    label="Reading Time"
                    value={`~${document.metadata.readingTimeMinutes} min`}
                  />
                  {document.metadata.readabilityLevel && (
                    <InfoItem
                      label="Readability"
                      value={document.metadata.readabilityLevel}
                      valueClassName={getReadabilityColor(document.metadata.readabilityLevel)}
                    />
                  )}
                </div>
              </div>

              {/* Processing Info */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Processing Details</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Chunking Strategy</p>
                    <p className="text-white font-medium">{document.metadata.chunkingStrategy}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {getStrategyDescription(document.metadata.chunkingStrategy)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content Characteristics */}
              {(document.metadata.hasCode ||
                document.metadata.hasTables ||
                document.metadata.hasLists ||
                document.metadata.hasLinks ||
                document.metadata.hasNumbers) && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Content Characteristics</h3>
                  <div className="flex flex-wrap gap-2">
                    {document.metadata.hasCode && <Badge icon="💻" label="Contains Code" />}
                    {document.metadata.hasTables && <Badge icon="📊" label="Contains Tables" />}
                    {document.metadata.hasLists && <Badge icon="📝" label="Contains Lists" />}
                    {document.metadata.hasLinks && <Badge icon="🔗" label="Contains Links" />}
                    {document.metadata.hasNumbers && <Badge icon="🔢" label="Data-Rich" />}
                  </div>
                </div>
              )}

              {/* Key Phrases */}
              {document.metadata.keyPhrases && document.metadata.keyPhrases.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Key Phrases</h3>
                  <div className="flex flex-wrap gap-2">
                    {document.metadata.keyPhrases.map((phrase, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm"
                      >
                        {phrase}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Words */}
              {document.metadata.topWords && document.metadata.topWords.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Most Frequent Words</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {document.metadata.topWords.slice(0, 9).map((item, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-800 rounded px-3 py-2">
                        <span className="text-gray-300 text-sm">{item.word}</span>
                        <span className="text-gray-500 text-xs">{item.count}×</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface InfoItemProps {
  label: string;
  value: string;
  valueClassName?: string;
}

function InfoItem({ label, value, valueClassName = 'text-white' }: InfoItemProps) {
  return (
    <div>
      <p className="text-sm text-gray-400 mb-1">{label}</p>
      <p className={`font-medium ${valueClassName}`}>{value}</p>
    </div>
  );
}

interface BadgeProps {
  icon: string;
  label: string;
}

function Badge({ icon, label }: BadgeProps) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-300 rounded-lg text-sm">
      <span>{icon}</span>
      <span>{label}</span>
    </span>
  );
}
